import React from 'react';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PerformanceTimeline = ({ monthlyData, isLoading }) => {
  if (isLoading) {
    return <Skeleton height={400} style={{ borderRadius: '8px' }} />;
  }

  if (!monthlyData || monthlyData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ color: '#aaa' }}>
          No data available
        </Typography>
      </Box>
    );
  }

  // Prepare chart data
  const labels = monthlyData.map(month => {
    const monthName = dayjs().month(month.month).format('MMM');
    return `${monthName} ${month.year}`;
  });

  const datasets = [
    {
      label: 'Deposits',
      data: monthlyData.map(m => m.deposits),
      borderColor: '#2af598',
      backgroundColor: 'rgba(42, 245, 152, 0.1)',
      tension: 0.4,
      fill: false
    },
    {
      label: 'Withdrawals',
      data: monthlyData.map(m => m.withdrawals),
      borderColor: '#f52a2d',
      backgroundColor: 'rgba(245, 42, 45, 0.1)',
      tension: 0.4,
      fill: false
    },
    {
      label: 'Net Performance',
      data: monthlyData.map(m => m.netPerformance),
      borderColor: '#2196f3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      tension: 0.4,
      fill: false
    },
    {
      label: 'AUM',
      data: monthlyData.map(m => m.aum),
      borderColor: '#ffc107',
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
      tension: 0.4,
      fill: true,
      yAxisID: 'y1'
    }
  ];

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          font: {
            size: 12
          },
          usePointStyle: true,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#2af598',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 11
          },
          callback: function(value) {
            return '$' + (value / 1000).toFixed(0) + 'K';
          }
        },
        title: {
          display: true,
          text: 'Transactions ($)',
          color: '#ccc'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#ffc107',
          font: {
            size: 11
          },
          callback: function(value) {
            return '$' + (value / 1000000).toFixed(1) + 'M';
          }
        },
        title: {
          display: true,
          text: 'AUM ($)',
          color: '#ffc107'
        }
      }
    }
  };

  const data = {
    labels,
    datasets
  };

  return (
    <Box sx={{ height: 400 }}>
      <Line options={options} data={data} />
    </Box>
  );
};

export default PerformanceTimeline;

